import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { CopyIdButton, MobileSortControl } from '@/components/shared';
import { useMediaQuery } from '@/lib/use-media-query';
import { formatDateTime, formatShortId } from '../format';
import type {
  BackofficeCommunicationResponse,
  SortDirection,
  UserEmailCommunicationSortBy,
} from '../types';

const SORTABLE_HEADERS: Record<UserEmailCommunicationSortBy, string> = {
  id: 'Message ID',
  subject: 'Subject',
  templateKey: 'Template',
  status: 'Status',
  channelStatus: 'Channel status',
  providerMessageId: 'Provider message ID',
  createdAt: 'Created',
  sentAt: 'Sent',
};

const MOBILE_SORT_OPTIONS = Object.entries(SORTABLE_HEADERS).map(([value, label]) => ({
  value: value as UserEmailCommunicationSortBy,
  label,
}));

export interface UserEmailCommunicationsTableProps {
  communications: BackofficeCommunicationResponse[];
  sortBy: UserEmailCommunicationSortBy;
  sortDirection: SortDirection;
  onSortChange: (sortBy: UserEmailCommunicationSortBy) => void;
  onSelect: (communication: BackofficeCommunicationResponse) => void;
}

function SortButton({
  label,
  field,
  sortBy,
  sortDirection,
  onSortChange,
}: {
  label: string;
  field: UserEmailCommunicationSortBy;
  sortBy: UserEmailCommunicationSortBy;
  sortDirection: SortDirection;
  onSortChange: (sortBy: UserEmailCommunicationSortBy) => void;
}) {
  const active = field === sortBy;
  const Icon = active ? (sortDirection === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;

  return (
    <button
      type="button"
      className="focus-ring inline-flex items-center gap-1 rounded-md text-left font-semibold text-neutral-600"
      aria-label={`Sort by ${label}`}
      onClick={() => onSortChange(field)}
    >
      <span>{label}</span>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
}

export function UserEmailCommunicationsTable({
  communications,
  sortBy,
  sortDirection,
  onSortChange,
  onSelect,
}: UserEmailCommunicationsTableProps) {
  const isMobile = useMediaQuery('(max-width: 767px)');

  if (isMobile) {
    return (
      <div>
        <MobileSortControl
          label="Sort email communications"
          options={MOBILE_SORT_OPTIONS}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={onSortChange}
        />
        <div className="divide-y divide-neutral-100">
          {communications.map((communication) => (
            <article
              key={communication.id}
              role="button"
              tabIndex={0}
              aria-label={`Open email ${formatShortId(communication.id)}`}
              className="cursor-pointer px-4 py-4 hover:bg-backoffice-50 focus:bg-backoffice-50 focus:outline-none"
              onClick={() => onSelect(communication)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(communication);
                }
              }}
            >
              <div className="text-body font-semibold text-neutral-900">
                {communication.email?.subjectRendered ?? communication.templateKey}
              </div>
              <div className="mt-1 text-small text-neutral-600">
                {communication.status} | Sent {formatDateTime(communication.sentAt)}
              </div>
            </article>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table
        className="min-w-[1320px] w-full border-collapse text-left"
        aria-label="Email communications"
      >
        <thead className="border-b border-neutral-200 bg-white text-small text-neutral-500">
          <tr>
            {Object.entries(SORTABLE_HEADERS).map(([field, label]) => (
              <th key={field} scope="col" className="min-w-[150px] px-4 py-3">
                <SortButton
                  label={label}
                  field={field as UserEmailCommunicationSortBy}
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSortChange={onSortChange}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100 text-body">
          {communications.map((communication) => (
            <tr
              key={communication.id}
              tabIndex={0}
              aria-label={`Open email ${formatShortId(communication.id)}`}
              className="cursor-pointer hover:bg-backoffice-50 focus:bg-backoffice-50 focus:outline-none"
              onClick={() => onSelect(communication)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(communication);
                }
              }}
            >
              <td className="px-4 py-4">
                <div className="group inline-flex items-center gap-2">
                  <span className="font-medium tabular-nums text-neutral-700">
                    {formatShortId(communication.id)}
                  </span>
                  <CopyIdButton value={communication.id} label="Copy message ID" />
                </div>
              </td>
              <td className="px-4 py-4 text-neutral-900">
                {communication.email?.subjectRendered ?? 'No subject'}
              </td>
              <td className="px-4 py-4 text-neutral-700">{communication.templateKey}</td>
              <td className="px-4 py-4 text-neutral-700">{communication.status}</td>
              <td className="px-4 py-4 text-neutral-700">
                {communication.email?.channelSpecificStatus ?? 'Not available'}
              </td>
              <td className="px-4 py-4 text-neutral-700">
                {communication.email?.smtpProviderMessageId ?? 'Not available'}
              </td>
              <td className="px-4 py-4 text-neutral-700">
                {formatDateTime(communication.createdAt)}
              </td>
              <td className="px-4 py-4 text-neutral-700">{formatDateTime(communication.sentAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
